import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch, Stack,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import { EditIcon, SafetyVestIcon, TrashIcon } from '../../../common/Icons';
import { atualizaTamanhoEVariacao, deletaTamanhoEVariacao, listaTamanhosEVariacoes } from './configs/functions';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { SizeForm } from './SizeForm';
import {ItemsNoCodeTableFilter} from "../../management/organization/components/ItemsNoCodeTableFilter";
import {FiltersButton} from "../../../common/FiltersButton";
import {useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {useAppContext} from "../../../../contexts/AppContext";
import {getSaldoMaquinaData} from "../reports/configs/functions";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import Alert from "@mui/material/Alert";

const SizeRow = ({ data, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData, hasContract }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);

    async function handleToggleStatus() {
        if (!hasPermission(["admin", "admin_variacoes", "update_variacoes"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaTamanhoEVariacao(data.id, { status: !active })
            if (res) {
                const message = active ? 'Variação inativada com sucesso' : 'Variação ativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.id, !data.status)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
            console.log(error)
        }
    }

    const deleteCostCenter = () => {
        const id = data.id;
        deletaTamanhoEVariacao(id)
            .then((response) => {
                const message = response.data.message ?? 'Variação deletada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
                updateData();
            }).catch((error) => {
                const message = error.response.data.message ?? 'Erro ao executar operação'
                handleExibirAlerta('Erro', message, 'error');
            })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow
                key={data.id}
                aria-disabled={!hasContract}
                sx={{
                    opacity: !hasContract ? 0.5 : 1,
                    pointerEvents: !hasContract ? "none" : "auto",
                    backgroundColor: !hasContract ? "action.disabledBackground" : "inherit"
                }}
            >
                <TableCell>{data.tipo}</TableCell>
                <TableCell>{data.descricao}</TableCell>
                <TableCell>{data.observacao}</TableCell>

                <TableCell>
                    <Switch
                        checked={active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_variacoes", "update_variacoes"])}
                    />
                </TableCell>

                <TableCell align="right" sx={{ minWidth: 110 }}>
                    {hasPermission(["admin", "admin_variacoes", "update_variacoes"]) && (
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            <EditIcon />
                        </IconButton>
                    )}

                    {hasPermission(["admin", "admin_variacoes", "delete_variacoes"]) && (
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>
                    )}
                </TableCell>
            </TableRow>
        </>
    );
}

export function ListOfSizes() {
    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

    // estados para controle de paginacao
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);

    const [sizes, setSizes] = useState(null);
    const [hasContract, setHasContract] = useState(false);

    const [openForm, setOpenForm] = useState(false);
    const [selectedCostCenter, setSelectedCostCenter] = useState(null);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
        tipo: searchParams.get('nome'),
        viewOnly: true,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKeySizes = ['listOfSizes', queryFilters];
    const { data: sizeData, isLoading, error: sizeError, isFetching, refetch } = useQuery({
        queryKey: queryKeySizes,
        queryFn: async () => {
            const response = await listaTamanhosEVariacoes(queryFilters);
            return response.data;
        },
        enabled: !!searchParamPage && !!searchParamPerPage,
    });

    function handleEditCostCenter(item) {
        setSelectedCostCenter(item);
        setOpenForm(true);
    }

    const updateCostCenterStatus = (categoryId, status) => setSizes(
        sizes.map((item) => {
            if (item.id === categoryId) {
                item.status = status;
            }
            return item;
        })
    );

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_variacoes", "list_variacoes"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    useEffect(() => {
        if (sizeData) {
            setSizes(sizeData.data);
            setNumTotalItems(sizeData.numero_total);
            setHasContract(sizeData.hasContract);
        }
    }, [sizeData]);

    useEffect(() => {
        if (openForm) return;
        setSelectedCostCenter(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    return (<>
        <Helmet title="Tamanhos e Variações" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="end"
            spacing={3}
        >
            <Grid item xs={12} md={8}>
                <Breadcrumbs>
                    <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao >
                    <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao >
                    <LinkNavegacao to='/epis/produtos'>Produtos</LinkNavegacao >
                    <Typography variant='span'>Tamanhos e Variações</Typography>
                </Breadcrumbs>
                <PageTitle
                    icon={<SafetyVestIcon fontSize='large' />}
                    title='Tamanhos e Variações'
                    description='Administre os Tamanhos e Variações disponíveis para seus produtos.'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    <Grid item xs={12} md={4}>
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                                disabled={
                                    !hasPermission(["admin", "admin_variacoes", "create_variacoes"])
                                    || !hasContract
                                }
                            >
                                Cadastrar Variação
                            </Button>
                        </Typography>
                    </Grid>
                </Stack>
            </Grid>

            <ItemsNoCodeTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
                item={"Tamanho/Variação"}
            />

            <Grid item xs={12}>
                {!hasContract && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Contrato não encontrado ou está inativo para esta empresa.
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <Table aria-label="Variações">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tamanho/Variação</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Observação</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                    <SkeletonTableRow
                                        numCells={4}
                                        numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                                    />
                                ) : sizes && sizes.length > 0 ? (
                                    sizes.map((item) => (
                                        <SizeRow
                                            key={item.id}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditCostCenter}
                                            handleUpdateStatus={updateCostCenterStatus}
                                            updateData={refetch}
                                            hasContract={hasContract}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={5} />
                                )
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </TableContainer>
            </Grid>
        </Grid>

        <SizeForm
            open={openForm}
            setOpen={setOpenForm}
            selectedCostCenter={selectedCostCenter}
            empresas={empresasData?.data?.data || []}
            updateData={refetch}
        />
    </>);
}